import { formatPricePerMTok } from '../../utils/formatUtils';
import type { Model } from '../../types';

/**
 * The Prompt caching section, one of three states by the Model's capability.
 * A cache price the catalog does not carry falls back to the input price, as
 * usage metering does.
 */
export function PromptCachingSection({
  model,
  enabled,
  onChange,
}: {
  model: Model;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const title = (
    <span id="prompt-caching-label" className="block text-sm font-medium text-gray-900 dark:text-white">
      Prompt caching
    </span>
  );

  if (model.promptCaching === 'explicit') {
    const readPrice = formatPricePerMTok(model.cacheReadPriceMTok ?? model.inputPriceMTok);
    const writePrice = formatPricePerMTok(model.cacheWritePriceMTok ?? model.inputPriceMTok);
    return (
      <div data-testid="prompt-caching-section" className="rounded-md border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-labelledby="prompt-caching-label"
            data-testid="prompt-caching-toggle"
            onClick={() => onChange(!enabled)}
            className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              enabled ? 'bg-indigo-500' : 'bg-zinc-300 dark:bg-zinc-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                enabled ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <div className="flex-1">
            {title}
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              The agent's prompts and tools are cached for 5 minutes after each call. Reads cost {readPrice}/MTok; the
              first write costs {writePrice}/MTok. Prompts shorter than the model's minimum are sent uncached at no
              extra cost.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="prompt-caching-section" className="rounded-md border border-gray-200 dark:border-gray-700 px-4 py-3">
      {title}
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {model.promptCaching === 'automatic'
          ? 'This provider caches automatically for prompts over ~1,000 tokens. No setting required.'
          : 'Prompt caching is not available for this provider.'}
      </p>
    </div>
  );
}

export default PromptCachingSection;
